import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../../__generated__/gql";
import Modal from "../../../../../ui/Modal/Modal";
import {
  CellContext,
  PaginationState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import Table from "../../../../../ui/Table/Table";
import styles from "./ScrapeWebpagesModal.module.scss";
import { Search } from "../../../../../ui/Icon/Icon";
import TextInput from "../../../../../ui/TextInput/TextInput";
import Truncate from "../../../../../ui/Truncate/Truncate";
import Checkbox from "../../../../../ui/Checkbox/Checkbox";

const IncludeWebpageInReferenceMutation = graphql(/* GraphQL */ `
  mutation includeWebpageInReference($id: String!) {
    includeWebpageInReference(id: $id) {
      id
      includedInReference
    }
  }
`);

const ExcludeWebpageFromReferenceMutation = graphql(/* GraphQL */ `
  mutation excludeWebpageFromReference($id: String!) {
    excludeWebpageFromReference(id: $id) {
      id
      includedInReference
    }
  }
`);

const IncludeWebpageInInternalLinkingMutation = graphql(/* GraphQL */ `
  mutation includeWebpageInInternalLinking($id: String!) {
    includeWebpageInInternalLinking(id: $id) {
      id
      includedInInternalLinking
      includedInPriorityLinking
    }
  }
`);

const ExcludeWebpageFromInternalLinkingMutation = graphql(/* GraphQL */ `
  mutation excludeWebpageFromInternalLinking($id: String!) {
    excludeWebpageFromInternalLinking(id: $id) {
      id
      includedInInternalLinking
      includedInPriorityLinking
    }
  }
`);

const IncludeWebpageInPriorityLinkingMutation = graphql(/* GraphQL */ `
  mutation includeWebpageInPriorityLinking($id: String!) {
    includeWebpageInPriorityLinking(id: $id) {
      id
      includedInInternalLinking
      includedInPriorityLinking
    }
  }
`);

const ExcludeWebpageFromPriorityLinkingMutation = graphql(/* GraphQL */ `
  mutation excludeWebpageFromPriorityLinking($id: String!) {
    excludeWebpageFromPriorityLinking(id: $id) {
      id
      includedInInternalLinking
      includedInPriorityLinking
    }
  }
`);

const ScrapeWebpagesModalQuery = graphql(/* GraphQL */ `
  query scrapedWebpages(
    $input: ScrapedWebpagesInput
    $paginationOptions: OffsetPaginationOptions
  ) {
    scrapedWebpages(input: $input, paginationOptions: $paginationOptions) {
      nodes {
        id
        url
        title
        includedInReference
        includedInInternalLinking
        includedInPriorityLinking
        body
        summary
      }
      totalCount
    }
  }
`);

const DEFAULT_PAGINATION_STATE: PaginationState = {
  pageIndex: 0,
  pageSize: 25,
};

type ScrapedWebpage = {
  id: string;
  url?: string | null;
  title?: string | null;
  includedInReference?: boolean | null;
  includedInInternalLinking?: boolean | null;
  includedInPriorityLinking?: boolean | null;
  summary?: string | null;
};

function InReferenceCell({
  cell,
}: CellContext<ScrapedWebpage, ScrapedWebpage["includedInReference"]>) {
  const [, includeWebpageInReference] = useMutation(
    IncludeWebpageInReferenceMutation,
  );
  const [, excludeWebpageFromReference] = useMutation(
    ExcludeWebpageFromReferenceMutation,
  );

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    if (checked) {
      await includeWebpageInReference({ id: cell.row.original.id });
    } else {
      await excludeWebpageFromReference({ id: cell.row.original.id });
    }
  };

  return (
    <Checkbox
      checked={cell.getValue() ?? false}
      onChange={onChange}
      disabled={!cell.row.original.summary}
    />
  );
}

function InLinkingCell({
  cell,
}: CellContext<ScrapedWebpage, ScrapedWebpage["includedInInternalLinking"]>) {
  const [, includeWebpageInInternalLinking] = useMutation(
    IncludeWebpageInInternalLinkingMutation,
  );
  const [, excludeWebpageFromInternalLinking] = useMutation(
    ExcludeWebpageFromInternalLinkingMutation,
  );

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    if (checked) {
      await includeWebpageInInternalLinking({ id: cell.row.original.id });
    } else {
      await excludeWebpageFromInternalLinking({ id: cell.row.original.id });
    }
  };

  return (
    <Checkbox
      checked={cell.getValue() ?? false}
      onChange={onChange}
      disabled={!cell.row.original.summary}
    />
  );
}

function InPriorityLinking({
  cell,
}: CellContext<ScrapedWebpage, ScrapedWebpage["includedInPriorityLinking"]>) {
  const [, includeWebpageInPriorityLinking] = useMutation(
    IncludeWebpageInPriorityLinkingMutation,
  );
  const [, excludeWebpageFromPriorityLinking] = useMutation(
    ExcludeWebpageFromPriorityLinkingMutation,
  );

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    if (checked) {
      await includeWebpageInPriorityLinking({ id: cell.row.original.id });
    } else {
      await excludeWebpageFromPriorityLinking({ id: cell.row.original.id });
    }
  };

  return (
    <Checkbox
      checked={cell.getValue() ?? false}
      onChange={onChange}
      disabled={!cell.row.original.summary}
    />
  );
}

function InternalScrapeWebpagesModal({ isOpen }: { isOpen: boolean }) {
  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );
  const [keyPhrase, setKeyPhrase] = useState<string>("");
  const [debouncedKeyPhrase, setDebouncedKeyPhrase] = useState<string>("");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPagination(DEFAULT_PAGINATION_STATE);
      setDebouncedKeyPhrase(keyPhrase);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [keyPhrase]);

  const [scrapedWebpagesResponse] = useQuery({
    pause: !isOpen,
    query: ScrapeWebpagesModalQuery,
    requestPolicy: "network-only",
    variables: {
      input: {
        keyPhrase: debouncedKeyPhrase,
      },
      paginationOptions: {
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
      },
    },
  });

  const loading = scrapedWebpagesResponse.fetching;
  const totalCount =
    scrapedWebpagesResponse.data?.scrapedWebpages.totalCount ?? 0;

  const scrapedWebpages: ScrapedWebpage[] = useMemo(
    () => scrapedWebpagesResponse.data?.scrapedWebpages.nodes ?? [],
    [scrapedWebpagesResponse.data?.scrapedWebpages.nodes],
  );

  const columnHelper = createColumnHelper<ScrapedWebpage>();

  const columns = [
    columnHelper.accessor("title", {
      header: "Title",
      cell: ({ cell }) => (
        <Truncate>
          {cell.row.original.summary ? cell.getValue() : "Unable to scrape"}
        </Truncate>
      ),
      minSize: 288,
    }),
    columnHelper.accessor("url", {
      header: "URL",
      cell: ({ cell }) => {
        const value = cell.getValue();

        return (
          <Truncate>
            {typeof value === "string" ? value.replaceAll("/", "/\u200B") : ""}
          </Truncate>
        );
      },
      size: 305,
    }),
    columnHelper.accessor("includedInReference", {
      header: "Included in reference",
      cell: InReferenceCell,
      size: 170,
    }),
    columnHelper.accessor("includedInInternalLinking", {
      header: "Included in linking",
      cell: InLinkingCell,
      size: 170,
    }),
    columnHelper.accessor("includedInPriorityLinking", {
      header: "Priority linking page",
      cell: InPriorityLinking,
      size: 170,
    }),
    columnHelper.accessor("summary", {
      header: "Summary",
      size: 305,
      cell: ({ cell }) => <Truncate>{cell.getValue()}</Truncate>,
    }),
  ];

  return (
    <Table
      className={styles.table}
      columns={columns}
      data={scrapedWebpages}
      loading={loading}
      pagination={pagination}
      setPagination={setPagination}
      totalCount={totalCount}
      resourceName={{ plural: "webpages", singular: "webpage" }}
      clearSelectionOnChange={false}
      Header={
        <div className={styles.header}>
          <TextInput
            leftSection={<Search size="medium" />}
            onChange={(event) => {
              setKeyPhrase(event.currentTarget.value);
            }}
            placeholder="Search pages"
            value={keyPhrase}
            className={styles.search}
          />
        </div>
      }
    />
  );
}

function ScrapeWebpagesModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      size="full-width"
      isOpen={isOpen}
      onClose={onClose}
      title="Website pages"
      classNames={{ body: styles.body }}
      fullHeight
    >
      <InternalScrapeWebpagesModal isOpen={isOpen} />
    </Modal>
  );
}

export default ScrapeWebpagesModal;
